import { useEffect, useState } from 'react';
import {
  REJECTION_REASON_LABELS,
  rejectionReasonSchema,
  type RejectionReason,
} from '@lizrd/contracts';
import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { Select } from './ui/select';

const REASON_OPTIONS = rejectionReasonSchema.options.map((value) => ({
  value,
  label: REJECTION_REASON_LABELS[value],
}));

/**
 * Reject confirmation that also collects *why* — a mandatory reason (dropdown)
 * and an optional free-text note — so the model can learn from declines. The
 * Reject button stays disabled until a reason is chosen.
 */
export function RejectRecommendationModal({
  open,
  title,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: RejectionReason, note: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  // Start each rejection fresh.
  useEffect(() => {
    if (open) {
      setReason('');
      setNote('');
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={
        <h3 className="font-display text-base font-bold text-bone">Reject this recommendation?</h3>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={!reason || pending}
            onClick={() => reason && onConfirm(reason as RejectionReason, note.trim())}
          >
            {pending ? 'Rejecting…' : 'Reject'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          {title ? (
            <>
              “{title}” will move to Rejected and drop out of your open list. You can reopen it
              later if you change your mind.
            </>
          ) : null}
        </p>

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
            Reason <span className="text-danger">*</span>
          </label>
          <Select
            value={reason}
            onChange={setReason}
            options={REASON_OPTIONS}
            placeholder="Select a reason…"
            ariaLabel="Rejection reason"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
            Note <span className="font-normal normal-case text-muted">(optional)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Add any detail that would help us improve…"
            className="w-full resize-y rounded-sm border border-border bg-surface-3 px-3 py-2 text-sm text-bone focus:border-iris focus:outline-none focus:ring-2 focus:ring-iris-dim"
          />
        </div>
      </div>
    </Modal>
  );
}
